import { BeforeInsert, BeforeUpdate, Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { type CampaignStepEventStatus, isOpenStepEventStatus } from '../../constants/campaign-sequence';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignMembershipEntity } from './campaign-membership.entity';

/**
 * PD-17613 — one person's row for one step: the work queue and the history.
 * Each member has at most ONE open row (pending, due or sending) saying what
 * is due and when; finished rows are the history. Campaign work is not tasks
 * (Chris, 10/26/26): these rows are what the campaign's work view reads.
 *
 * "One open row per member" is enforced by the database: `openMembershipId`
 * holds the member's id while the row is open and NULL once it is done or
 * skipped, under a unique index (MySQL allows any number of NULLs in a
 * unique index). The hooks below keep it in step with `status`, so every
 * entity-path save maintains it.
 *
 * Not auditable: engine-maintained and high volume; the rows are themselves
 * the history.
 */
@Index('campaign_step_event__open_membership', ['openMembershipId'], { unique: true })
@Index('campaign_step_event__membership', ['fkCampaignMembershipId', 'stepNo'], {})
@Index('campaign_step_event__campaign_status_due', ['fkCampaignId', 'status', 'dueAt'], {})
@Index('campaign_step_event__status_due', ['status', 'dueAt'], {})
@Index('campaign_step_event__external_message', ['externalMessageId'], {})
@Entity('campaign_step_event', {}, { auditable: false })
export class CampaignStepEventEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	@Column('int', { name: 'fk_campaign_membership__id', unsigned: true })
	public fkCampaignMembershipId!: number;

	@Column('int', { name: 'fk_campaign_step__id', unsigned: true })
	public fkCampaignStepId!: number;

	/** The step's position when this row was made (the step may move later). */
	@Column('int', { name: 'stepNo', unsigned: true })
	public stepNo!: number;

	// App-validated (CampaignStepType), copied from the step.
	@Column('varchar', { name: 'stepType', length: 20 })
	public stepType!: string;

	// App-validated (CampaignStepEventStatus).
	@Column('varchar', { name: 'status', length: 20, default: () => "'pending'" })
	public status!: CampaignStepEventStatus | string;

	/** The member id while this row is open, NULL once closed (see the class comment). */
	@Column('int', { name: 'openMembershipId', unsigned: true, nullable: true })
	public openMembershipId!: number | null;

	/** The normal due time (from the wait after the previous step). */
	@Column('datetime', { name: 'dueAt', nullable: true })
	public dueAt!: Date | null;

	/**
	 * A scheduled call-back: a task (`_event`) linked to this member. While it
	 * is open its time overrides `dueAt`; deleting it puts the step back on
	 * its normal date.
	 */
	@Column('int', { name: 'callbackEventId', unsigned: true, nullable: true })
	public callbackEventId!: number | null;

	@Column('datetime', { name: 'doneAt', nullable: true })
	public doneAt!: Date | null;

	/** What happened: a call outcome name, 'sent', a reply category, why it was skipped. */
	@Column('varchar', { name: 'outcome', length: 100, nullable: true })
	public outcome!: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	@Column('int', { name: 'fk_call__id', unsigned: true, nullable: true })
	public fkCallId!: number | null;

	/** The Instantly email id of a sent email. */
	@Column('varchar', { name: 'externalMessageId', length: 255, nullable: true })
	public externalMessageId!: string | null;

	@Column('int', { name: 'fk_user__id_doneBy', unsigned: true, nullable: true })
	public fkUserIdDoneBy!: number | null;

	/** Engine bookkeeping (an email's release phase, the mailbox that sent it, ...). */
	@Column('json', { name: 'detail', nullable: true })
	public detail!: Record<string, unknown> | null;

	@ManyToOne(() => CampaignMembershipEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign_membership__id', referencedColumnName: 'id' }])
	public fkCampaignMembership!: CampaignMembershipEntity;

	@BeforeInsert()
	@BeforeUpdate()
	public syncOpenMembershipId(): void {
		this.openMembershipId = isOpenStepEventStatus(this.status) ? this.fkCampaignMembershipId : null;
	}
}
